import { SelectFactory, SelectTypes, Option } from '@g17eco/molecules/select/SelectFactory';
import { SingleValue } from 'react-select';
import type { ReportDocumentUtrv } from '@api/initiative-report-documents';
import { getFormattedSurveyDate } from '@utils/survey';
import type { SelectedUtrv } from './types';
import { QUESTION } from '@constants/terminology';

interface MetricSelectorProps {
  utrvs: ReportDocumentUtrv[];
  selectedUtrv?: SelectedUtrv;
  onUtrvChange: (utrv: SelectedUtrv | undefined) => void;
}

const getDisplayText = (utrv: ReportDocumentUtrv) =>
  `${utrv.surveyName ?? ''} ${getFormattedSurveyDate({ effectiveDate: utrv.effectiveDate, period: utrv.period })}`;

export const MetricSelector = ({ utrvs, selectedUtrv, onUtrvChange }: MetricSelectorProps) => {
  const utrvOptions = utrvs.map((utrv) => ({
    value: utrv._id,
    label: (
      <div className='d-flex flex-row align-items-center w-100 py-1'>
        <div className='flex-fill'>
          <div className='fw-semibold text-truncate'>{utrv.universalTracker.name}</div>
          <div className='fst-italic text-sm text-truncate'>{getDisplayText(utrv)}</div>
        </div>
      </div>
    ),
  }));

  const valueListCodeOptions = (selectedUtrv?.universalTracker.valueValidation?.table?.columns || []).map((column) => ({
    value: column.code,
    label: column.name || column.code,
  }));

  const handleUtrvChange = (option: SingleValue<Option>) => {
    const utrv = utrvs.find((u) => u._id === option?.value);
    if (!utrv) {
      onUtrvChange(undefined);
      return;
    }

    onUtrvChange({
      ...selectedUtrv,
      utrvId: utrv._id,
      universalTracker: utrv.universalTracker,
    });
  };

  const handleValueListCodeChange = (option: SingleValue<Option>) => {
    if (!selectedUtrv) {
      return;
    }

    onUtrvChange({
      ...selectedUtrv,
      valueListCode: option?.value ?? undefined,
    });
  };

  const selectedValueListCode =
    valueListCodeOptions.find((option) => option.value === selectedUtrv?.valueListCode) ?? null;

  return (
    <>
      <div data-testid='metric-select'>
        <SelectFactory
          selectType={SelectTypes.SingleSelect}
          className={'mb-3'}
          placeholder={`Select ${QUESTION.SINGULAR}`}
          value={
            selectedUtrv
              ? {
                  label: selectedUtrv.universalTracker.name,
                  value: selectedUtrv.utrvId,
                }
              : undefined
          }
          options={utrvOptions}
          onChange={handleUtrvChange}
          isClearable
        />
      </div>
      <SelectFactory
        selectType={SelectTypes.SingleSelect}
        className={'mb-3'}
        placeholder='Select input'
        options={valueListCodeOptions}
        value={selectedValueListCode}
        onChange={handleValueListCodeChange}
        isDisabled={!selectedUtrv || valueListCodeOptions.length === 0}
        isClearable
      />
    </>
  );
};
